<template>
  <section
    id="unique-value"
    class="relative py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-800 dark:to-slate-900"
  >
    <div class="container relative">
      <!-- Header -->
      <div class="text-center">
        <p class="mt-4 mb-2 text-sm font-semibold tracking-widest uppercase text-primary">
          {{ t('unique_h1') }}
        </p>
        <h2
          class="text-3xl font-extrabold leading-normal md:text-4xl text-slate-900 dark:text-white"
        >
          {{ t('unique_h2') }}
        </h2>

        <p class="max-w-2xl mx-auto mt-3 text-slate-600 dark:text-slate-300">
          {{ t('unique_p') }}
        </p>
      </div>

      <!-- Cards -->
      <div
        class="grid grid-cols-1 gap-6 mt-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
      >
        <div
          v-for="(item, i) in values"
          :key="'uv-' + i"
          class="relative flex flex-col items-center h-full p-6 overflow-hidden transition-all duration-300 rounded-2xl group bg-white/90 dark:bg-slate-900/90 ring-1 ring-primary/10 dark:ring-primary/15 hover:-translate-y-1 hover:shadow-xl hover:ring-primary/30"
        >
          <!-- Icon halo (brand gradient) -->
          <div class="relative">
            <span
              class="absolute inset-0 mx-auto rounded-full -z-10 size-16 blur-2xl bg-gradient-to-tr from-primary/20 to-secondary/30"
            ></span>
            <div
              class="flex items-center justify-center rounded-full w-14 h-14 bg-secondary/40 text-primary"
            >
              <i :class="item.icon" class="text-2xl"></i>
            </div>
          </div>

          <!-- Title -->
          <h3
            class="text-lg font-semibold text-center text-slate-900 dark:text-white group-hover:text-primary"
          >
            {{ t(item.key) }}
          </h3>

          <!-- Faint background icon (brand tint) -->
          <i
            :class="[item.icon, 'pointer-events-none absolute -end-10 -bottom-6 text-[120px] opacity-[0.07] dark:opacity-[0.08] text-primary']"
          ></i>

          <!-- Subtle focus ring on hover (brand) -->
          <span
            class="absolute inset-0 pointer-events-none rounded-2xl ring-1 ring-transparent group-hover:ring-primary/25"
          ></span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { messages } from '@/i18n';

export default {
  data() {
    const initial =
      (document.documentElement.lang || localStorage.getItem('app_lang') || 'ar').toLowerCase();

    return {
      messages,
      lang: initial.startsWith('en') ? 'en' : 'ar',
      _langObserver: null,
      _langHandler: null,

      // Titles pulled from i18n keys (no descriptions)
      values: [
        { icon: 'mdi mdi-brain',                 key: 'uv_tech' },       // Technical intelligence in a unified solution
        { icon: 'mdi mdi-layers-triple-outline', key: 'uv_inclusive' },  // Inclusiveness
        { icon: 'mdi mdi-cash-multiple',         key: 'uv_price' },      // Price flexibility
        { icon: 'mdi mdi-speedometer',           key: 'uv_speed' },      // Speed
        { icon: 'mdi mdi-account-tie',           key: 'uv_expertise' },  // Specialized expertise
      ],
    };
  },

  mounted() {
    // Live update (navbar dispatches 'app:lang')
    this._langHandler = (e) => {
      const next = (e.detail || 'ar').toLowerCase().startsWith('en') ? 'en' : 'ar';
      if (next !== this.lang) this.lang = next;
    };
    window.addEventListener('app:lang', this._langHandler, { passive: true });

    // Fallback: watch <html lang/dir>
    this._langObserver = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === 'attributes' && (m.attributeName === 'lang' || m.attributeName === 'dir')) {
          const htmlLang = (document.documentElement.lang || 'ar').toLowerCase();
          const next = htmlLang.startsWith('en') ? 'en' : 'ar';
          if (next !== this.lang) this.lang = next;
        }
      }
    });
    this._langObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang', 'dir'],
    });
  },

  beforeUnmount() {
    if (this._langObserver) this._langObserver.disconnect();
    if (this._langHandler) window.removeEventListener('app:lang', this._langHandler);
  },

  methods: {
    t(key) {
      return (this.messages[this.lang] && this.messages[this.lang][key]) || key;
    },
  },
};
</script>
